import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { batch, useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { isNil } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { VIDEO_PLATFORM_DISPLAY_NAME } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/appointmentModalityConstants'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { DEFAULT_VIDEO_PLATFORM, VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'
import { ButtonModifier } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/baseButton/BaseButton'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { IconButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/iconButton/IconButton'
import { EditIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/EditIcon'
import { ShieldTriageIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ShieldTriageIcon'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { Tooltip } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'
import { InlineBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineBanner/InlineBanner'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { useGetVideoSessionPreferenceQuery, usePutVideoSessionPreferenceMutation } from './settingsApi'
import { UpdateUpcomingAvailabilitiesModalityModal } from './UpdateUpcomingAvailabilitiesModalityModal'
import { UpdateUpcomingSessionsModalityModal } from './UpdateUpcomingSessionsModalityModal'
import { UpdatingSessionsModalityModal } from './UpdatingSessionsModalityModal'
import { VideoSessionPreferenceEditModal } from './VideoSessionPreferenceEditModal'
import { actions as mixpanelActions, mixpanelEvents } from '../../../mixpanel/mixpanelConstants'
import { track } from '../../../mixpanel/mixpanelTracking'
import { useUpdateCalendarEventsVideoPlatformMutation } from '../calendar/data/calendarApi'
import { SETTINGS } from '../common/constants/routingConstants'
import { getAuthConfig, getAuthUserId } from '../data/auth/authSelectors'
import { useAppDispatch } from '../data/store'
import {
  toBulkUpdatePayload,
  useGetAppointmentsQuery,
  useUpdateAppointmentsBulkMutation,
} from '../lyraTherapy/clients/clientDetails/data/appointmentsApi'
import { clearToastContent, setToastContent } from '../lyraTherapy/data/ltToastAutoActions'

const Container = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  border: `1px solid ${theme.colors.borderDefault}`,
  padding: theme.spacing['24px'],
  borderRadius: '16px',
  flexDirection: 'column',
  gap: theme.spacing['16px'],
}))

const Row = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '16px',
})

const RowText = styled.View({
  flexDirection: 'column',
  gap: '4px',
  flex: 1,
})

const LabelContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '4px',
})

const PencilIconButton = styled(IconButton)({
  alignSelf: 'center',
})

export const VideoSessionPreferenceSettings: FunctionComponent = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { colors } = useTheme() as ThemeType
  const userId = useSelector(getAuthUserId)

  const config = useSelector(getAuthConfig)
  const { isDAEngageOnOffPlatformVideoEnabled } = useFlags()
  const [searchParams, setSearchParams] = useSearchParams()
  const [editModalVisible, setEditModalVisible] = useState(false)

  const {
    data: preference,
    isLoading: isLoadingVideoSessionPreference,
    isError,
    error,
  } = useGetVideoSessionPreferenceQuery(
    { id: userId },
    { skip: !isDAEngageOnOffPlatformVideoEnabled || !userId || !config?.supportsOnOffPlatformVideo },
  )
  const hasPreferenceFetchError = isError && (error as { response?: { status?: number } })?.response?.status !== 404
  const [putVideoSessionPreference] = usePutVideoSessionPreferenceMutation()
  const [updateAppointmentsBulk] = useUpdateAppointmentsBulkMutation()
  const [updateCalendarEventsVideoPlatform] = useUpdateCalendarEventsVideoPlatformMutation()
  const { data: allAppointments = [], isLoading: isLoadingAppointments } = useGetAppointmentsQuery(
    { providerId: userId },
    { skip: !editModalVisible },
  )

  useEffect(() => {
    if (searchParams.get('openVideoPreference') === 'true') {
      setEditModalVisible(true)
      searchParams.delete('openVideoPreference')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])
  const [updateModalVisible, setUpdateModalVisible] = useState(false)
  const [updateAvailabilitiesModalVisible, setUpdateAvailabilitiesModalVisible] = useState(false)
  const [updatingModalVisible, setUpdatingModalVisible] = useState(false)
  const [pendingPlatform, setPendingPlatform] = useState<VIDEO_PLATFORM>(DEFAULT_VIDEO_PLATFORM)
  const currentPlatform = preference ?? DEFAULT_VIDEO_PLATFORM

  const upcomingVideoSessions = useMemo(
    () =>
      allAppointments.filter(
        (a) =>
          a.meetingFormat === MEETING_FORMATS.VIDEO &&
          !a.pastAppointment &&
          ['newAppt', 'rescheduled'].includes(a.appointmentStatus),
      ),
    [allAppointments],
  )

  const sessionsToUpdate = useMemo(
    () => upcomingVideoSessions.filter((a) => (a.videoPlatform ?? DEFAULT_VIDEO_PLATFORM) !== pendingPlatform),
    [upcomingVideoSessions, pendingPlatform],
  )

  const videoPreferenceInfo = {
    [VIDEO_PLATFORM.LYRA]: {
      title: VIDEO_PLATFORM_DISPLAY_NAME.LYRA,
      description:
        'Lyra automatically sends your clients secure video links and reminder notifications for each session. You and your client join sessions from the HIPAA compliant Lyra platform.',
    },
    [VIDEO_PLATFORM.EXTERNAL]: {
      title: VIDEO_PLATFORM_DISPLAY_NAME.EXTERNAL,
      description:
        'Lyra notifies clients that you use an external video platform. You are responsible for manually sending meeting links to clients for every session.',
    },
  }

  const rollback = async (originalPlatform: VIDEO_PLATFORM) => {
    try {
      await putVideoSessionPreference({
        id: userId,
        data: { value: originalPlatform, value_type: 'string' },
      }).unwrap()
    } catch {
      // ignore rollback failure
    }
  }

  const dispatchSuccessToast = ({ isAvailabilityOnly = false }: { isAvailabilityOnly?: boolean } = {}) => {
    dispatch(
      setToastContent({
        text: isAvailabilityOnly
          ? 'Video preference saved. Availability may take a few minutes to update.'
          : 'Video preference saved. Sessions and availability may take a few minutes to update.',
        id: 'videoSessionPreferenceUpdated',
        toastType: 'success',
      }),
    )
  }

  const dispatchRetryErrorToast = () => {
    dispatch(
      setToastContent({
        text: (
          <>
            {"Something went wrong. We couldn't update the video preference of some sessions or availability. "}
            <Link
              text='Go to settings'
              testID={tID('videoSessionPreferenceRetrySettingsLink')}
              color={colors.components.toast.error.text}
              underline
              medium
              size={BodyTextSize.SMALL}
              onPress={() => {
                dispatch(clearToastContent())
                navigate(`${SETTINGS.route}?openVideoPreference=true`)
              }}
            />
          </>
        ),
        id: 'videoSessionPreferenceRetryUpdateError',
        toastType: 'error',
        persistent: true,
      }),
    )
  }

  const dispatchErrorToast = ({
    onRetry,
    originalPlatform,
    isAvailabilityOnly = false,
  }: {
    onRetry: () => Promise<void>
    originalPlatform: VIDEO_PLATFORM
    isAvailabilityOnly?: boolean
  }) => {
    let retrying = false
    dispatch(
      setToastContent({
        text: (
          <>
            {"Something went wrong. We couldn't update video preference. "}
            <Link
              text='Try again'
              testID={tID('videoSessionPreferenceSettingsLink')}
              color={colors.components.toast.error.text}
              underline
              medium
              size={BodyTextSize.SMALL}
              onPress={async () => {
                if (retrying) return
                retrying = true
                try {
                  await onRetry()
                  batch(() => {
                    dispatch(clearToastContent())
                    dispatchSuccessToast({ isAvailabilityOnly })
                  })
                } catch {
                  await rollback(originalPlatform)
                  batch(() => {
                    dispatch(clearToastContent())
                    dispatchRetryErrorToast()
                  })
                } finally {
                  retrying = false
                }
              }}
            />
          </>
        ),
        id: 'videoSessionPreferenceUpdateError',
        toastType: 'error',
        persistent: true,
      }),
    )
  }

  const handlePlatformChange = async (platform: VIDEO_PLATFORM) => {
    if (platform === currentPlatform && !isNil(preference)) {
      setEditModalVisible(false)
      return
    }
    if (hasPreferenceFetchError) {
      setEditModalVisible(false)
      return
    }
    track({ event: mixpanelEvents.BUTTON_PRESS, action: mixpanelActions.VIDEO_SESSION_PREFERENCE_CONTINUE })
    setPendingPlatform(platform)
    setEditModalVisible(false)
    const hasSessionsToUpdate = upcomingVideoSessions.some(
      (a) => (a.videoPlatform ?? DEFAULT_VIDEO_PLATFORM) !== platform,
    )
    if (hasSessionsToUpdate) {
      setUpdateModalVisible(true)
    } else {
      setUpdateAvailabilitiesModalVisible(true)
    }
  }

  const handleUpdateSessions = async (appointmentIds: number[]) => {
    track({
      event: mixpanelEvents.BUTTON_PRESS,
      action: mixpanelActions.VIDEO_SESSION_PREFERENCE_APPLY_TO_UPCOMING_SESSIONS_UPDATE,
    })
    setUpdateModalVisible(false)
    setUpdatingModalVisible(true)
    const originalPlatform = currentPlatform

    const updatePreference = () =>
      putVideoSessionPreference({
        id: userId,
        data: { value: pendingPlatform, value_type: 'string' },
      }).unwrap()

    const updateAppointments = () =>
      updateAppointmentsBulk({
        providerId: userId,
        data: toBulkUpdatePayload(allAppointments, appointmentIds, {
          meetingFormat: MEETING_FORMATS.VIDEO,
          videoPlatform: pendingPlatform,
        }),
      }).unwrap()

    const updateCalendar = () =>
      updateCalendarEventsVideoPlatform({ provider_id: userId, platform: pendingPlatform }).unwrap()

    const results = await Promise.allSettled([updatePreference(), updateAppointments(), updateCalendar()])

    if (results.every((r) => r.status === 'fulfilled')) {
      dispatchSuccessToast()
    } else {
      await rollback(originalPlatform)
      const failedCalls = [updateAppointments, updateCalendar].filter((_, i) => results[i + 1].status === 'rejected')
      setUpdatingModalVisible(false)
      dispatchErrorToast({
        onRetry: async () => {
          const retryResults = await Promise.allSettled([updatePreference(), ...failedCalls.map((fn) => fn())])
          if (retryResults.some((result) => result.status === 'rejected')) {
            throw new Error('Retry failed')
          }
        },
        originalPlatform,
      })
    }
  }

  const handleSkip = async () => {
    setUpdateModalVisible(false)
    setUpdateAvailabilitiesModalVisible(false)
  }

  const handleUpdateAvailabilities = async () => {
    setUpdateModalVisible(false)
    setUpdateAvailabilitiesModalVisible(false)
    setUpdatingModalVisible(true)
    const originalPlatform = currentPlatform

    const updatePreference = () =>
      putVideoSessionPreference({
        id: userId,
        data: { value: pendingPlatform, value_type: 'string' },
      }).unwrap()

    const updateCalendar = () =>
      updateCalendarEventsVideoPlatform({ provider_id: userId, platform: pendingPlatform }).unwrap()

    const results = await Promise.allSettled([updatePreference(), updateCalendar()])

    if (results.every((r) => r.status === 'fulfilled')) {
      dispatchSuccessToast({ isAvailabilityOnly: true })
    } else {
      await rollback(originalPlatform)
      const failedCalls = [updateCalendar].filter((_, i) => results[i + 1].status === 'rejected')
      setUpdatingModalVisible(false)
      dispatchErrorToast({
        onRetry: async () => {
          const retryResults = await Promise.allSettled([updatePreference(), ...failedCalls.map((fn) => fn())])
          if (retryResults.some((result) => result.status === 'rejected')) {
            throw new Error('Retry failed')
          }
        },
        originalPlatform,
        isAvailabilityOnly: true,
      })
    }
  }

  if (isLoadingVideoSessionPreference) {
    return null
  }

  return (
    <>
      <Container testID={tID('VideoSessionPreferenceSettings')}>
        {currentPlatform === VIDEO_PLATFORM.EXTERNAL && (
          <InlineBanner
            backgroundColor={colors.components.inlineInfoBanner.background}
            textColor={colors.components.inlineInfoBanner.text}
            icon={<ShieldTriageIcon size={24} color={colors.components.inlineInfoBanner.infoIcon.fill} />}
            title='Switch to Lyra Engage to host HIPAA compliant video sessions at no cost'
            text='Lyra automatically sends your clients secure video links and reminder notifications for each session. You and your client join sessions from the HIPAA compliant Lyra platform.'
            marginBottom='0'
            testId='VideoSessionPreferenceSettings-lyra-engage-banner'
          />
        )}
        <Row>
          <RowText>
            <LabelContainer>
              <Title size={TitleSize.XSMALL} text={VIDEO_PLATFORM_DISPLAY_NAME[currentPlatform]} />
              {currentPlatform === VIDEO_PLATFORM.LYRA && (
                <Tooltip content='HIPAA Compliant' hoverEnabled placement='top'>
                  <ShieldTriageIcon size={20} color={colors.radioWithOutlinePrimaryTextDefault} />
                </Tooltip>
              )}
            </LabelContainer>
            <BodyText color={colors.textSecondary} text={videoPreferenceInfo[currentPlatform].description} wrap />
          </RowText>
          <PencilIconButton
            onPress={() => {
              track({ event: mixpanelEvents.BUTTON_PRESS, action: mixpanelActions.VIDEO_SESSION_PREFERENCE_EDIT })
              setEditModalVisible(true)
            }}
            leftIcon={<EditIcon size={24} isFilled={false} />}
            iconColor={colors.iconActive}
            modifier={ButtonModifier.UNSTYLED}
            accessibilityLabel='Edit video session preference'
            testID={tID('VideoSessionPreferenceSettings-edit-button')}
          />
        </Row>
      </Container>
      <VideoSessionPreferenceEditModal
        visible={editModalVisible}
        currentPlatform={currentPlatform}
        isLoadingAppointments={isLoadingAppointments}
        onRequestClose={() => {
          track({ event: mixpanelEvents.BUTTON_PRESS, action: mixpanelActions.VIDEO_SESSION_PREFERENCE_CANCEL })
          setEditModalVisible(false)
        }}
        onSave={handlePlatformChange}
      />
      <UpdateUpcomingSessionsModalityModal
        visible={updateModalVisible}
        sessions={sessionsToUpdate}
        oldPlatform={currentPlatform}
        newPlatform={pendingPlatform}
        onRequestClose={() => {
          track({
            event: mixpanelEvents.BUTTON_PRESS,
            action: mixpanelActions.VIDEO_SESSION_PREFERENCE_APPLY_TO_UPCOMING_SESSIONS_CANCEL,
          })
          setUpdateModalVisible(false)
        }}
        onUpdateSessions={handleUpdateSessions}
        onUpdateAvailability={() => {
          track({
            event: mixpanelEvents.BUTTON_PRESS,
            action: mixpanelActions.VIDEO_SESSION_PREFERENCE_APPLY_TO_UPCOMING_SESSIONS_UPDATE_AVAILABILITIES_ONLY,
          })
          handleUpdateAvailabilities()
        }}
        onSkip={() => {
          track({
            event: mixpanelEvents.BUTTON_PRESS,
            action: mixpanelActions.VIDEO_SESSION_PREFERENCE_APPLY_TO_UPCOMING_SESSIONS_CANCEL,
          })
          handleSkip()
        }}
      />
      <UpdateUpcomingAvailabilitiesModalityModal
        visible={updateAvailabilitiesModalVisible}
        oldPlatform={currentPlatform}
        newPlatform={pendingPlatform}
        onRequestClose={() => {
          track({
            event: mixpanelEvents.BUTTON_PRESS,
            action: mixpanelActions.VIDEO_SESSION_PREFERENCE_APPLY_TO_UPCOMING_AVAILABILITIES_CANCEL,
          })
          setUpdateAvailabilitiesModalVisible(false)
        }}
        onUpdate={() => {
          track({
            event: mixpanelEvents.BUTTON_PRESS,
            action: mixpanelActions.VIDEO_SESSION_PREFERENCE_APPLY_TO_UPCOMING_AVAILABILITIES_UPDATE,
          })
          handleUpdateAvailabilities()
        }}
        onSkip={() => {
          track({
            event: mixpanelEvents.BUTTON_PRESS,
            action: mixpanelActions.VIDEO_SESSION_PREFERENCE_APPLY_TO_UPCOMING_AVAILABILITIES_CANCEL,
          })
          handleSkip()
        }}
      />
      <UpdatingSessionsModalityModal
        visible={updatingModalVisible}
        onRequestClose={() => setUpdatingModalVisible(false)}
      />
    </>
  )
}
